import "amazon-connect-streams";

/** A US MDN is 10 digits. Anything else is not a subscriber number we can use. */
const MDN_DIGITS = 10;

/**
 * Reads the MDN the IVR collected from the caller.
 *
 * The AT&T IVR writes it to the **uppercase** `MDN` contact attribute on voice (10 of the 12
 * `UpdateContactAttributes` actions across the AT&T production flows). Amazon Connect attribute keys
 * are case-sensitive, so the lowercase `mdn` read on the voice path only ever matched the
 * chat-channel attribute and was dead on every call, which is why voice fell through to the raw
 * caller endpoint: literally "Anonymous" / "Restricted" / "Unavailable" on blocked caller ID, and a
 * toll-free or trunk number on transferred legs.
 *
 * Only a plausible 10-digit value is accepted, so a junk IVR value cannot simply replace a junk
 * endpoint. The result is returned in the same `+1`-prefixed shape the endpoint fallback produces,
 * so downstream `removeCountryCode` and blocklist matching behave identically for both sources.
 *
 * The attribute map is deliberately *not* case-normalized: `PARTNER` and `WHISPER` are read in exact
 * uppercase elsewhere and other consumers depend on exact-case keys.
 */
export const getIvrCollectedMdn = (attributes: connect.AttributeDictionary | undefined): string | undefined => {
    const digits = attributes?.MDN?.value?.replace(/\D/g, "") ?? "";
    // The IVR usually writes a bare 10-digit number, but accept the US country code so a value
    // written as "+16155550142" is not discarded in favour of the junk caller endpoint.
    const national = digits.length === MDN_DIGITS + 1 && digits.startsWith("1") ? digits.slice(1) : digits;
    return national.length === MDN_DIGITS ? `+1${national}` : undefined;
};
